import {
    Button, ButtonVariants, CustomClaimValue, Facility, getGroundTruthJobStatusDisplayName,
    GroundTruthJob, GroundTruthJobStatus,
} from "@app/shared";
import { useEffect, useMemo, useRef, useState } from "react";
import { Icons, LINK_BUTTON, PageContainer, SortableTable, SortableTableHeaderType } from "../../../shared";
import { describeResult } from "../groundTruthSummary";
import {
    asOfColumn, countColumn, detailColumn, photosColumn, statusColumn, uploadedColumn,
} from "./groundTruthColumns";

type GroundTruthPanelProps = {
    facility: Facility;
    jobs: GroundTruthJob[] | undefined;
    isLoading: boolean;
    timeZone: string;
    timeZoneLabel: string;
    onUpload: () => void;
    /**
     * Bumped when something elsewhere (the map overlay's "Establish baseline" link) wants the user
     * brought here. Scrolls the panel into view and pulses the action, rather than opening the
     * dialog from under them — they asked where to go, not to be teleported.
     */
    highlightSignal?: number;
    onShowDetail: (jobId: string) => void;
    onPromote: (job: GroundTruthJob) => void;
};

/**
 * The ground truth run history, and the entry point for uploading a new set. Lifted out of
 * FacilityParkingView so that file orchestrates panels rather than also owning their columns.
 */
export const GroundTruthPanel: React.FC<GroundTruthPanelProps> = ({
    facility, jobs, isLoading, timeZone, timeZoneLabel, onUpload, onShowDetail, onPromote, highlightSignal,
}) => {
    // On the button rather than a wrapper: Button forwards its ref, and the thing to scroll to is
    // the thing that pulses. A `display: contents` wrapper generates no box, so scrolling to one
    // does nothing.
    const updateButtonRef = useRef<HTMLButtonElement>(null);
    const [isPulsing, setIsPulsing] = useState(false);

    useEffect(() => {
        if (!highlightSignal) return;
        updateButtonRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        setIsPulsing(true);
        const timer = setTimeout(() => setIsPulsing(false), 2500);
        return () => clearTimeout(timer);
    }, [highlightSignal]);

    const columns = useMemo((): SortableTableHeaderType<GroundTruthJob>[] => [
        asOfColumn<GroundTruthJob>(timeZone, timeZoneLabel),
        uploadedColumn<GroundTruthJob>(timeZone, timeZoneLabel),
        photosColumn<GroundTruthJob>(),
        statusColumn<GroundTruthJob>(),
        countColumn<GroundTruthJob>(),
        {
            dataKey: "errorMessage", header: "Result", width: 420,
            renderFunc: (job) => {
                if (!job) return null;
                if (job.errorMessage) return <span className="text-red-600">{job.errorMessage}</span>;
                if (job.status !== GroundTruthJobStatus.Complete) return null;
                return <span>{describeResult(job, jobs ?? [])}</span>;
            },
        },
        {
            // Actions get their own columns so they line up down the table instead of floating at
            // the end of a sentence whose length varies per row.
            header: "", width: 110, sortable: false,
            renderFunc: (job) => {
                if (job?.status !== GroundTruthJobStatus.Complete) return null;
                // Promoting the same run twice writes a second identical baseline and moves
                // nothing, so the action retires once it has been used. Said plainly rather than
                // just disappearing — a row whose action vanished with no explanation reads as a
                // bug, and "why can't I click this one" is the question it would raise.
                if (job.promotedToBaseline) {
                    return <span className="text-muted-body-text whitespace-nowrap">Baseline set</span>;
                }
                return (
                    <button
                        type="button"
                        className={`text-link-page hover:text-link-page-hover underline whitespace-nowrap ${LINK_BUTTON}`}
                        onClick={() => onPromote(job)}
                    >
                        Use as baseline
                    </button>
                );
            },
        },
        detailColumn<GroundTruthJob>(job => job.id, onShowDetail),
    ], [timeZone, timeZoneLabel, jobs, onShowDetail, onPromote]);

    return (
        <PageContainer
            title="Ground Truth"
            Icon={Icons.Photo}
            titleRight={
                <Button
                    ref={updateButtonRef}
                    variant={ButtonVariants.Primary}
                    onClick={onUpload}
                    className={isPulsing ? "animate-pulse ring-2 ring-brand-primary ring-offset-2" : undefined}
                >
                    Update Ground Truth
                </Button>
            }
        >
            <div className="h-full overflow-y-auto">
                <SortableTable
                    data={jobs}
                    columns={columns}
                    initialSortKey="asOfDate"
                    initialSortDir="desc"
                    isLoading={isLoading}
                    emptyValue="No ground truth sets uploaded yet."
                />
            </div>
        </PageContainer>
    );
};
